import { useEffect, useMemo, useState } from "react";
import { Popper, Stack, Typography } from "@mui/material";
import type { PopperProps } from "@mui/material/Popper";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { GoMakeAutoComplate, GoMakeDialog } from "@/components";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { FONT_FAMILY } from "@/utils/font-family";
import { useTranslation } from "react-i18next";

const ASSIGN_TYPES_MENU_Z_INDEX = 1000010;

const AssignTypesSelectPopper = (props: PopperProps) => {
  const { anchorEl, style, ...other } = props;
  const anchorWidth =
    anchorEl && "clientWidth" in anchorEl ? anchorEl.clientWidth : undefined;

  return (
    <Popper
      {...other}
      anchorEl={anchorEl}
      style={{
        ...style,
        width: anchorWidth,
        minWidth: anchorWidth,
        zIndex: ASSIGN_TYPES_MENU_Z_INDEX,
      }}
    />
  );
};

export interface IAssignFileTypeOption {
  name: string;
}

export interface IFileTypeAssignment {
  file: File;
  skus: string[];
}

interface AssignFilesToTypesDialogProps {
  open: boolean;
  files: File[];
  types: IAssignFileTypeOption[];
  isConfirming?: boolean;
  onConfirm: (assignments: IFileTypeAssignment[]) => void;
  onCancel: () => void;
}

const getFileKey = (file: File, index: number) =>
  `${file.name}-${file.size}-${file.lastModified}-${index}`;

const AssignFilesToTypesDialog = ({
  open,
  files,
  types,
  isConfirming = false,
  onConfirm,
  onCancel,
}: AssignFilesToTypesDialogProps) => {
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction");
  const { primaryColor } = useGomakeTheme();
  const [assignments, setAssignments] = useState<Record<string, string>>({});

  const typeOptions = useMemo(
    () =>
      types.map((type) => ({
        label: type.name,
        value: type.name,
      })),
    [types]
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const initial: Record<string, string> = {};
    files.forEach((file, index) => {
      initial[getFileKey(file, index)] = "";
    });
    setAssignments(initial);
  }, [open, files]);

  const handleTypeChange = (fileKey: string, typeName: string) => {
    setAssignments((current) => ({
      ...current,
      [fileKey]: typeName,
    }));
  };

  const hasAnyLinkedType = useMemo(
    () =>
      files.some((file, index) =>
        Boolean(assignments[getFileKey(file, index)])
      ),
    [assignments, files]
  );

  const handleConfirm = () => {
    if (!hasAnyLinkedType) {
      return;
    }
    onConfirm(
      files.map((file, index) => {
        const key = getFileKey(file, index);
        return {
          file,
          skus: assignments[key] ? [assignments[key]] : [],
        };
      })
    );
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <GoMakeDialog
      open={open}
      onClose={handleCancel}
      onCancel={handleCancel}
      title={t("fileUploader.assignFilesToTypes")}
      primaryButtonLabel={t("fileUploader.uploadAndLink")}
      cancelButtonLabel={t("fileUploader.uploadWithoutLinkingButton")}
      onPrimaryAction={handleConfirm}
      primaryButtonLoading={isConfirming}
      primaryButtonDisabled={
        isConfirming || files.length === 0 || !hasAnyLinkedType
      }
      cancelButtonDisabled={isConfirming}
      maxWidth="md"
      customStyles={{ content: { padding: "12px 16px" } }}
      sx={{ zIndex: 1000002 }}
    >
      <Stack dir={dir} gap="12px">
        <Typography
          sx={{
            ...FONT_FAMILY.Inter(400, 13),
            color: "#667085",
            textAlign: dir === "rtl" ? "right" : "left",
          }}
        >
          {t("fileUploader.uploadWithoutLinking")}
        </Typography>
        <Stack
          className="custom-scroller"
          gap="8px"
          sx={{ maxHeight: 360, overflowY: "auto" }}
        >
          {files.map((file, index) => {
            const fileKey = getFileKey(file, index);
            const selectedType = assignments[fileKey]
              ? typeOptions.find(
                  (option) => option.value === assignments[fileKey]
                ) || null
              : null;
            return (
              <Stack
                key={fileKey}
                direction="row"
                alignItems="center"
                gap="12px"
                sx={{
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: "1px solid #EAECF0",
                  background: "#FFF",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap="8px"
                  sx={{ flex: "1 1 45%", minWidth: 0 }}
                >
                  <InsertDriveFileOutlinedIcon
                    sx={{
                      fontSize: 18,
                      color: primaryColor(500),
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    title={file.name}
                    sx={{
                      ...FONT_FAMILY.Inter(500, 13),
                      color: "#1A1A1A",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textAlign: dir === "rtl" ? "right" : "left",
                    }}
                  >
                    {file.name}
                  </Typography>
                </Stack>
                <Stack sx={{ flex: "1 1 55%", minWidth: 0 }}>
                  <GoMakeAutoComplate
                    options={typeOptions}
                    value={selectedType}
                    onChange={(
                      _event: unknown,
                      option: { label: string; value: string } | null
                    ) => handleTypeChange(fileKey, option?.value || "")}
                    getOptionLabel={(option: { label?: string }) =>
                      option?.label || ""
                    }
                    isOptionEqualToValue={(
                      option: { value: string },
                      value: { value: string }
                    ) => option?.value === value?.value}
                    placeholder={t("fileUploader.linkToTypes")}
                    disabled={isConfirming}
                    withArrow
                    disablePortal={false}
                    PopperComponent={AssignTypesSelectPopper}
                    style={{ width: "100%", height: 40, minWidth: 180 }}
                    componentsProps={{
                      popper: {
                        dir,
                        sx: { zIndex: `${ASSIGN_TYPES_MENU_Z_INDEX} !important` },
                      },
                      paper: {
                        sx: {
                          width: "100%",
                          direction: dir,
                          "& .MuiAutocomplete-listbox": { width: "100%" },
                          "& .MuiAutocomplete-option": {
                            direction: dir,
                            textAlign: dir === "rtl" ? "right" : "left",
                            justifyContent:
                              dir === "rtl" ? "flex-end" : "flex-start",
                          },
                        },
                      },
                    }}
                  />
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </GoMakeDialog>
  );
};

export { AssignFilesToTypesDialog };
